import { Component, EventEmitter, Input, Output } from '@angular/core';
import { luminance, toHex6 } from '../report-aggregation';

/** One coloured box. `field` and `labelField` are the keys a change is patched back onto. */
export interface SummaryBox {
  field: string;
  label: string;
  /** Set only when the label itself is editable; absent means the label is fixed. */
  labelField?: string;
  /** What is typed on the block, if anything. */
  override: string;
  /** What the data says, blank when nothing can be derived. */
  computed: string;
  background: string;
  color: string;
}

/** Shown when a figure is neither computed nor supplied, rather than an empty box. */
export const NO_VALUE = '—';

/**
 * Same rule as the deck's own buttons: a client's palette can be light enough that white text
 * disappears, so the text colour comes from the background's luminance rather than being fixed.
 */
export function textOn(hex: string, dark = 'var(--ecr-ink, #1b2b33)', light = 'var(--ecr-light-ink, #ffffff)'): string {
  if (!toHex6(hex)) return light;
  return luminance(hex) > 150 ? dark : light;
}

/**
 * The row of coloured figure boxes, shared by the modules that lead with headline numbers rather
 * than a chart. Purely presentational: each module decides what its boxes are, which of them have a
 * computed value and whether their labels can be renamed.
 */
@Component({
  selector: 'app-summary-boxes',
  standalone: false,
  template: `
    <div class="summary-grid">
      @for (box of boxes; track box.field) {
        <div class="summary-box p-3 text-center"
             [style.background-color]="box.background"
             [style.color]="box.color">
          @if (editing) {
            <!-- Edited on the box itself rather than in the module popover: these are the content of
                 the slide, not settings about how it is drawn. -->
            <input type="text"
                   class="box-input figure font-weight-bold w-100 text-center mb-3"
                   maxlength="40"
                   [attr.aria-label]="box.label"
                   [attr.placeholder]="box.computed || NO_VALUE"
                   [value]="box.override"
                   (input)="valueChange.emit({ field: box.field, value: $any($event.target).value })">
          } @else {
            <p class="figure font-weight-bold mb-3">{{ value(box) }}</p>
          }

          @if (editing && box.labelField) {
            <input type="text"
                   class="box-input box-label w-100 text-center"
                   maxlength="40"
                   [attr.aria-label]="'Label for ' + box.label"
                   [attr.placeholder]="box.label"
                   [value]="box.label"
                   (input)="labelChange.emit({ field: box.labelField!, value: $any($event.target).value })">
          } @else {
            <p class="box-label mb-0">{{ box.label }}</p>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }
    .summary-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
      gap: 16px;
    }
    .summary-box {
      border-radius: 6px;
      /* A floor rather than a fixed height, so a wrapped label still grows the box. Centred
         vertically because the content no longer fills it. align-items stays at its stretch default,
         which is what keeps the edit inputs full width. */
      min-height: 8rem;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
    .figure {
      font-size: var(--ecr-font-kpi, 26px);
      line-height: 1.1;
      word-break: break-word;
    }
    .box-label {
      font-size: var(--ecr-font-xs, 11px);
    }
    /* currentColor so the box works on any palette: the text colour is already contrast-picked
       against the background, so the border and placeholder follow it automatically. */
    .box-input {
      background: transparent;
      border: 1px dashed currentColor;
      border-radius: 3px;
      color: inherit;
      padding: 0 2px;
    }
    .box-input::placeholder {
      color: currentColor;
      opacity: 0.6;
      font-weight: 400;
    }
    .box-input:focus {
      outline: none;
      border-style: solid;
    }
  `]
})
export class SummaryBoxesComponent {
  @Input({ required: true }) boxes: SummaryBox[] = [];
  @Input() editing?: boolean;

  @Output() valueChange = new EventEmitter<{ field: string; value: string }>();
  @Output() labelChange = new EventEmitter<{ field: string; value: string }>();

  readonly NO_VALUE = NO_VALUE;

  value(box: SummaryBox): string {
    return box.override.trim() || box.computed || NO_VALUE;
  }
}
